import type { CashSource, LoanCycleStatus, SavingsAccountStatus } from '@/api/collections-types'
import type { MeetingStatus } from '@/api/types'

/**
 * The facts collection capture is built on, written down once.
 *
 * Several of these mirror a constant on the server. Where they do, the server is authoritative and
 * the copy here exists only so the screen can refuse a value before an operator has keyed forty
 * lines behind it — never so the client can decide something the server did not.
 */

/**
 * How far back a value date may reach: three days, from
 * `SavingsPostingService.BackdatingWindowDays`.
 *
 * A paper sheet transcribed on Monday and a phone that found signal on Tuesday both describe money
 * that moved earlier, so backdating is a permitted, bounded practice rather than a mistake.
 *
 * The loans contract does not enforce this window — a repayment is only refused before its own
 * disbursement date — but a sheet carries one value date across all three columns, and dating the
 * savings lines differently from the loan lines on the same sheet would put the institution's
 * records at odds with the passbooks. So the tighter of the two rules governs the whole sheet.
 */
export const BACKDATING_WINDOW_DAYS = 3

/**
 * Cash taken at a meeting sits in the officer's field drawer, not a counter till.
 *
 * This is not a choice offered to the operator. A collection sheet is by definition money
 * collected in the field; a payment taken over the counter is posted from the teller screen, which
 * is where the drawer, the receipt printer, and the person handing the money over all are.
 */
export const COLLECTION_CASH_SOURCE: CashSource = 'FieldOfficerTill'

/** The three columns of a sheet, in the order the paper sheet carries them. */
export const COLLECTION_COLUMNS = ['LoanRepayment', 'SavingsDeposit', 'SecurityDeposit'] as const

export type CollectionColumn = (typeof COLLECTION_COLUMNS)[number]

export const COLUMN_LABELS: Record<CollectionColumn, string> = {
  LoanRepayment: 'Loan repayment',
  SavingsDeposit: 'Savings',
  SecurityDeposit: 'Security deposit',
}

export const COLUMN_SHORT_LABELS: Record<CollectionColumn, string> = {
  LoanRepayment: 'Repayment',
  SavingsDeposit: 'Savings',
  SecurityDeposit: 'Deposit',
}

/** What each column does, shown once above the grid rather than repeated on every row. */
export const COLUMN_DESCRIPTIONS: Record<CollectionColumn, string> = {
  LoanRepayment:
    'Applied to the member’s live round. The split across penalty, fees, interest and principal is computed on the server, not here.',
  SavingsDeposit: 'Taken into the member’s regular savings account.',
  SecurityDeposit:
    'Cash collateral against a loan. A separate account under a separate permission — taking it is not the same act as taking savings.',
}

/**
 * The meeting states a sheet may be keyed against.
 *
 * A collection is not gated on the register having been recorded — the money was collected whether
 * or not the register has been transcribed yet — but a cancelled meeting has no sheet at all.
 */
export const CAPTURABLE_MEETING_STATUSES = [
  'Scheduled',
  'InProgress',
  'Completed',
  'Missed',
] as const satisfies readonly MeetingStatus[]

export const isCapturable = (status: MeetingStatus): boolean =>
  (CAPTURABLE_MEETING_STATUSES as readonly MeetingStatus[]).includes(status)

/** The round states that can take a repayment. Anything else has no live balance to reduce. */
export const isLiveRound = (status: LoanCycleStatus): boolean => status === 'Active'

/**
 * Whether a savings account can take a deposit today.
 *
 * A dormant account is refused as `savings.account_dormant` and must be reactivated first; a
 * frozen one as `savings.account_frozen`. Saying so on the row is what stops an operator keying
 * the whole sheet and meeting the refusal on line nine.
 */
export const canTakeDeposit = (status: SavingsAccountStatus): boolean => status === 'Active'

export const SAVINGS_STATUS_EXPLANATION: Record<SavingsAccountStatus, string> = {
  Active: 'Open and accepting deposits.',
  Dormant: 'Dormant. It must be reactivated before it can transact.',
  Frozen: 'Frozen. A supervisor must release it before anything posts.',
  Closed: 'Closed. Nothing can be posted to it.',
}

/**
 * A time of day to the minute. The contract says `HH:mm`; .NET serialises `TimeOnly` as
 * `HH:mm:ss`, and a meeting does not start at ten o’clock and no seconds.
 */
export const formatMeetingTime = (value: string) => value.slice(0, 5)

/**
 * Whole days between two ISO dates, `later - earlier`.
 *
 * Both are parsed as UTC midnight so the arithmetic is over calendar days and cannot be moved by
 * an hour by anybody's clock. Ghana has no daylight saving, but a browser in another zone opening
 * this screen must still count the backdating window the same way.
 */
export function daysBetween(earlier: string, later: string): number {
  const from = Date.parse(`${earlier}T00:00:00Z`)
  const to = Date.parse(`${later}T00:00:00Z`)

  if (Number.isNaN(from) || Number.isNaN(to)) {
    return Number.NaN
  }

  return Math.round((to - from) / 86_400_000)
}

/** The earliest value date a sheet may carry, given the branch's open business date. */
export function earliestValueDate(businessDate: string): string {
  const parsed = Date.parse(`${businessDate}T00:00:00Z`)
  if (Number.isNaN(parsed)) {
    return businessDate
  }

  return new Date(parsed - BACKDATING_WINDOW_DAYS * 86_400_000).toISOString().slice(0, 10)
}

/** Whether a value date is inside the window the servers will accept for the branch's open day. */
export function isValueDateAllowed(valueDate: string, businessDate: string): boolean {
  const distance = daysBetween(valueDate, businessDate)
  return !Number.isNaN(distance) && distance >= 0 && distance <= BACKDATING_WINDOW_DAYS
}
